/*
 * Copyright (c) 2008-2020 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
 * This is CONFIDENTIAL code. Use is subject to license terms.
 */

/* global log */
import communication from "~/common/src/messaging" // or simply 'messaging' (but this is not detected by IDEA)
import { EventBus } from "~/common/src/event-bus" // or simply 'event-bus' (but this is not detected by IDEA)
import Vue from "vue"
import objectHash from "object-hash"
import store from "./index"

/**
 * Listen on meeting:connectFinished event which is triggered when the MCC has finished its high-level handshake in
 * order to resend current subscriptions on reconnects (after the backend was restarted while the MCC stayed open).
 * The event meeting:connectFinished is triggered when the InteractionsMessage was received from the backend.
 */
EventBus.$on("meeting:connectFinished", () => {
  store.dispatch("publications/resubscribeSubscriptionsOnReconnect")
})

// Plucked from various sources across the internet. Basic string hashing algo
const hash = function (...args) {
  return Array.from(args.join(""))
    .reduce((s, c) => (Math.imul(31, s) + c.charCodeAt(0)) | 0, 0)
    .toString()
}

export { hash }

export default {
  namespaced: true,

  state: {
    // map of subscription information hashed to config
    subscriptions: {},
    // As we don't have a reliable source of information whether we connected or reconnected we have to do it ourselves
    firstConnectionToServer: true,
  },

  mutations: {
    reconnectedToServer(state) {
      if (state.firstConnectionToServer === true) {
        // First connection to server
        Vue.set(state, "firstConnectionToServer", false)
      }
    },

    setPublicationSubscription(state, { keyHash, pubType, newChannelConfig, callback }) {
      Vue.set(state.subscriptions, keyHash, {
        callback,
        pubType,
        channelConfig: newChannelConfig,
        lastPublication: null,
      })
    },

    removePublicationSubscription(state, { keyHash }) {
      Vue.delete(state.subscriptions, keyHash)
    },

    setPublicationMessage(state, { keyHash, publication }) {
      if (!state.subscriptions[keyHash]) {
        log.error(`Attempting to set message for nonexistent subscription hash : ${keyHash}`)
        return
      }

      Vue.set(state.subscriptions[keyHash], "lastPublication", publication)
    },
  },

  actions: {
    /**
     * Called by the system when the MCC connects to a new backend-side connection. When the backend was restarted
     * while the MCC stayed open (any but the very first call of this action) any existing subscriptions in the store
     * will be resent to let the backend know what we are (still) interested in.
     */
    resubscribeSubscriptionsOnReconnect({ commit, getters, dispatch }) {
      if (getters["isFirstConnectionToServer"]) {
        log.debug("MCC connected for the first time - no need to resend current subscriptions")
        commit("reconnectedToServer") // From now on any calls to this action indicate the server was restarted
        return
      }

      let keyHashes = getters["getAllSubscriptionKeys"]
      if (keyHashes && keyHashes.length > 0) {
        log.debug("MCC reconnected - resubscribing", keyHashes.length, "subscriptions")
        keyHashes.forEach((keyHash) => {
          let subscription = getters["getSubscriptionConfig"](keyHash)
          log.debug("Resubscribing subscription", subscription.pubType)
          dispatch("subscribeToPublication", {
            pubType: subscription.pubType,
            newChannelConfig: subscription.channelConfig,
            callback: subscription.callback,
          })
        })
      }
    },

    /**
     * Configures a subscription to a specific publication type based on the channel configuration
     * provided. Stores basic information of the subscription including any callback to state.
     *
     * If a subscription configuration already exists in store, updates to the state information
     * are only treated as addition onto the existing subscription. Thus omitting configuration
     * elements should not be used as a means of removing state information.
     *
     * @param {String} pubType
     * @param {Object} channelConfig
     * @param {Function} callback
     */
    subscribeToPublication({ commit, getters }, { pubType, newChannelConfig, callback }) {
      // Both are needed to properly configure a subscription
      if (!pubType || !newChannelConfig) return

      const keyHash = hash(pubType, newChannelConfig?.meetingId)
      const newChannelHash = objectHash(newChannelConfig)

      const currentSubscription = getters["getSubscriptionConfig"](keyHash)
      let channels = []

      if (currentSubscription) {
        log.info(`Found subscription information for ${pubType} associated to hashed key: ${keyHash}`)

        // As the server associates both the config and the publication type to a subscription, additions to
        // an existing subscription must first remove the original subscription and then resubscribe. Otherwise
        // additions/updates must be treated as separate subscriptions and stored to state accordingly.
        const currentChannelHash = objectHash(currentSubscription.channelConfig)
        if (newChannelHash !== currentChannelHash) {
          log.info(
            `Channel configuration stored under ${keyHash} does not match requested : ${currentChannelHash.slice(
              0,
              8
            )}... != ${newChannelHash.slice(0, 8)}...`
          )

          log.info(`Adding request to unsubscribe from old channel config associated with : ${keyHash}`)

          channels.push({
            subscriptionTypeName: pubType,
            subscribe: false,
            channelConfig: currentSubscription.channelConfig,
          })

          log.info(`Continuing with subscription for ${pubType} associated to hashed key: ${keyHash}`)
        }

        // Overwrite callback with the one stored internally. If a new callback is desired, removed subscription entirely and reconfigure.
        callback = currentSubscription.callback
      }

      log.info(`Storing subscription information for ${pubType} to hashed key: ${keyHash}`)

      commit("setPublicationSubscription", { keyHash, pubType, newChannelConfig, callback })

      channels.push({
        subscriptionTypeName: pubType,
        subscribe: true,
        channelConfig: newChannelConfig,
      })

      // Finally send message
      communication.sendMessage(
        {
          channels,
        },
        "SubscriptionMessage"
      )
    },

    /**
     * Facilitates the process of unsubscribing from a specific publication type based on the channel
     * configuration provided. All associated state information is then purged.
     *
     * @param {String} pubType
     * @param {Object} targetChannelConfig
     */
    unsubscribeFromPublication({ commit, getters }, { pubType, targetChannelConfig }) {
      // Both are needed to properly process an unsubscription
      if (!pubType || !targetChannelConfig) return

      const keyHash = hash(pubType, targetChannelConfig?.meetingId)
      const targetChannelHash = objectHash(targetChannelConfig)

      const currentSubscription = getters["getSubscriptionConfig"](keyHash)
      if (!currentSubscription) {
        log.debug(`No subscription for ${pubType} is currently associated with hashed key : ${keyHash}`)
        return
      }
      const currentChannelHash = objectHash(currentSubscription.channelConfig)
      if (currentChannelHash !== targetChannelHash) {
        log.warn(
          `Channel configuration does not match for ${pubType} stored under ${keyHash} :`,
          `${currentChannelHash.slice(0, 8)}... != ${targetChannelHash.slice(0, 8)}... Using internal copy.`
        )
        targetChannelConfig = currentSubscription.channelConfig
      }

      log.info(`Removing subscription information for ${pubType} associated with hashed key : ${keyHash}`)

      commit("removePublicationSubscription", { keyHash })

      communication.sendMessage(
        {
          channels: [
            {
              subscriptionTypeName: pubType,
              subscribe: false,
              channelConfig: targetChannelConfig,
            },
          ],
        },
        "SubscriptionMessage"
      )
    },

    /**
     * Performs the processing of incoming publication messages, including informing any associated
     * callbacks. Updates state to hold the most recent publication received.
     *
     * @param {Object} message
     */
    processIncomingPublication({ commit, getters }, { message }) {
      for (const pub of message.publications) {
        let type = pub?.channel?.subscriptionTypeName

        if (!type) return

        let keyHash = hash(type, pub?.channel?.meetingId)

        log.info(`Processing new ${type} publication associated with hashed key : ${keyHash}`)

        let subscription = getters["getSubscriptionConfig"](keyHash)
        if (!subscription) {
          log.debug(`No subscription for ${type} is currently associated with hashed key : ${keyHash}`)
          return
        }

        commit("setPublicationMessage", { keyHash, publication: pub })

        let publicationCallback = subscription.callback
        if (publicationCallback) publicationCallback(getters["getSubscriptionConfig"](keyHash).lastPublication)
      }
    },
  },

  getters: {
    /**
     * Returns true if the MCC is either not yet connected to the server or the connection is in progress. Once
     * the MCC is fully connected to the server this getter returns false
     *
     * @param state
     * @returns {boolean}
     */
    isFirstConnectionToServer: (state) => state.firstConnectionToServer,

    /**
     * Returns the subscription config associated to a given hash.
     *
     * @param {String} keyHash
     * @returns {Object}
     */
    getSubscriptionConfig: (state) => (keyHash) => state.subscriptions[keyHash] || null,

    /**
     * Returns the hashes of the currently active subscriptions.
     *
     * @param state
     * @returns {[]}
     */
    getAllSubscriptionKeys: (state) => Object.keys(state.subscriptions),
  },
}
